import React, { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { APP_TO_BUILD } from "../../../appsConfig";
import moment from "moment";
import { useToggle } from "react-use";
import { useRootStore } from "@/state/RootStore";
import ExamBookingSection from "./ExamBookingSection";
import { captureEvent } from "@/utils/posthog";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../ui/dialog";
import { DialogContent } from "@radix-ui/react-dialog";
import { useClerk } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";
import { upsertProfile } from "@/api/fetch";
import { useTranslation } from "react-i18next";
import { showToastFromKey } from "@/lib/toast";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import DownloadAppCta, {
  DOWNLOAD_APP_CTA_CLEARANCE_CLASS,
} from "@/components/mobile/DownloadAppCta";
import { BasePage } from "@/components/BasePage";
import { observer } from "mobx-react";
import { requiresPaidProfileCompletion } from "@/utils/paidProfileCompletion";
import { BrandLogo } from "@/components/redesign/BrandLogo";
import { OnboardingHeader } from "@/components/redesign/OnboardingHeader";

// Each option carries an i18n `labelKey` for display and an English `value`
// that's persisted to the backend. Keeps server data locale-stable while
// letting the UI render localized labels.
export const KNOWLEDGE_LEVELS: { labelKey: string; value: string }[] =
  APP_TO_BUILD === "plumbing"
    ? [
        { labelKey: "knowledgeLevels.plumber_trainee", value: "Plumber Trainee" },
        { labelKey: "knowledgeLevels.apprentice_1", value: "1st Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_2", value: "2nd Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_3", value: "3rd Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_4", value: "4th Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_5", value: "5th Year Apprentice" },
        { labelKey: "knowledgeLevels.journeyman_plumber", value: "Journeyman Plumber" },
        { labelKey: "knowledgeLevels.master_plumber", value: "Master Plumber" },
        { labelKey: "knowledgeLevels.plumber_contractor", value: "Plumber Contractor" },
        { labelKey: "knowledgeLevels.plumber_non_licensed", value: "Plumber (Non-Licensed)" },
        { labelKey: "knowledgeLevels.none_of_the_above", value: "None of the Above" },
      ]
    : [
        { labelKey: "knowledgeLevels.electrical_trainee", value: "Electrical Trainee" },
        { labelKey: "knowledgeLevels.apprentice_1", value: "1st Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_2", value: "2nd Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_3", value: "3rd Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_4", value: "4th Year Apprentice" },
        { labelKey: "knowledgeLevels.apprentice_5", value: "5th Year Apprentice" },
        { labelKey: "knowledgeLevels.journeyman_electrician", value: "Journeyman Electrician" },
        { labelKey: "knowledgeLevels.master_electrician", value: "Master Electrician" },
        { labelKey: "knowledgeLevels.electrical_contractor", value: "Electrical Contractor" },
        { labelKey: "knowledgeLevels.electrician_non_licensed", value: "Electrician (Non-Licensed)" },
        { labelKey: "knowledgeLevels.none_of_the_above", value: "None of the Above" },
      ];

export const REFFERAL_VALUES: { labelKey: string; value: string }[] = [
  { labelKey: "referralOptions.google", value: "Google" },
  { labelKey: "referralOptions.app_store", value: "App Store" },
  { labelKey: "referralOptions.friend_colleague", value: "Friend/Colleague" },
  { labelKey: "referralOptions.instructor", value: "Instructor" },
  { labelKey: "referralOptions.through_my_school", value: "Through My School" },
  { labelKey: "referralOptions.student_ambassadors", value: "Student Ambassadors" },
  { labelKey: "referralOptions.posters", value: "Posters" },
  { labelKey: "referralOptions.online_ad", value: "Online Ad" },
  { labelKey: "referralOptions.reddit", value: "Reddit" },
  { labelKey: "referralOptions.discord", value: "Discord" },
  { labelKey: "referralOptions.other", value: "Other" },
];

function CreateProfileView({
  standalone,
  navigationLocked,
}: {
  standalone?: boolean;
  navigationLocked?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useRootStore();
  const { user } = useClerk();
  const isMobileWeb = useIsMobileWeb();
  const params = new URLSearchParams(location.search);
  const routeAfter = params.get("routeAfter");
  const emailOnly = params.get("emailOnly") === "true";
  const postProfileRoute = isMobileWeb ? "/profile" : "/study";

  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [welcomeModalOpen, toggleWelcomeModalOpen] = useToggle(false);
  const [hasBookedExam, setHasBookedExam] = useState<boolean | null>(null);
  const [examDateRaw, setExamDateRaw] = useState<string>("");

  const isAppleLogin = useMemo(
    () =>
      user?.externalAccounts?.some((account) => account.provider === "apple") ??
      false,
    [user?.externalAccounts]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailOnly) {
      if (hasBookedExam === null) {
        showToastFromKey({
          titleKey: "errors.generic_title",
          descriptionKey: "profileCreate.validation_exam_date",
          variant: "destructive",
        });
        return;
      }
      if (!examDateRaw) {
        showToastFromKey({
          titleKey: "errors.generic_title",
          descriptionKey: hasBookedExam
            ? "profileCreate.validation_select_exam_date"
            : "profileCreate.validation_select_estimated_month",
          variant: "destructive",
        });
        return;
      }
      store.mainAppStore.setHasBookedExam(hasBookedExam);
      if (hasBookedExam === true) {
        store.mainAppStore.setScheduledExamDate(
          moment(examDateRaw, "YYYY-MM-DD").format("MMMM D, YYYY")
        );
      } else {
        store.mainAppStore.setExamDateSelection(
          moment(examDateRaw, "YYYY-MM").format("MMMM, YYYY")
        );
      }
    }

    toggleIsSubmitting();

    if (!isAppleLogin) {
      store.mainAppStore.setProfileEmail(user?.emailAddresses[0].emailAddress);
    }

    try {
      const success = await store.mainAppStore.saveProfile({
        validateEmail: true,
      });
      if (!success) {
        toggleIsSubmitting(false);
        return;
      }

      if (emailOnly) {
        navigate(postProfileRoute);
        return;
      }

      if (routeAfter) {
        navigate(isMobileWeb ? "/profile" : `/${routeAfter}`);
      } else {
        toggleWelcomeModalOpen(true);
        captureEvent("new_user_created");
        upsertProfile({ sign_up_attribution: "web" });
      }
      captureEvent("created_profile", {
        profile_name: store.mainAppStore.profileName ?? null,
        profile_email: store.mainAppStore.profileEmail ?? null,
        profile_school: store.mainAppStore.profileSchool ?? null,
        exam_date_selection: store.mainAppStore.examDateSelection ?? null,
        profile_referral_source:
          store.mainAppStore.profileReferralSource ?? null,
      });
    } catch (error) {
      console.error("Profile creation error:", error);
      toggleIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Main Content */}
      <div
        className={cn(
          "grow flex justify-center",
          isMobileWeb
            ? cn(
                "min-h-screen w-full min-w-0 px-6 py-8",
                !navigationLocked && DOWNLOAD_APP_CTA_CLEARANCE_CLASS,
              )
            : navigationLocked ? "py-8" : "py-16"
        )}
      >
        {standalone && !isMobileWeb && (
          <BrandLogo className="absolute left-5 top-5" />
        )}
        <div className="flex w-full max-w-[594px] flex-col gap-[34px]">
          {/* Breadcrumb Navigation */}
          {!navigationLocked && (
            <div className="flex items-center gap-1.5">
              {!standalone && (
                <>
                  <span
                    className="text-zinc-500 text-sm font-normal font-inter cursor-pointer"
                    onClick={() => navigate("/profile")}
                  >
                    {t("profile.breadcrumb")}
                  </span>
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                </>
              )}
              <span
                className={cn(
                  "text-zinc-950 text-sm font-normal font-inter",
                  standalone && "text-xl font-bold"
                )}
              >
                {APP_TO_BUILD === "electrical_us" && !standalone
                  ? t("profileCreate.complete_profile_title")
                  : t("profile.create_profile")}
              </span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-[34px]">
            <div className="flex flex-col gap-4">
              {emailOnly ? (
                <div className="flex flex-col gap-1.5">
                  <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-email">
                    {t("profileCreate.email_label")}
                  </label>
                  <input
                    id="profile-email"
                    type="email"
                    placeholder={t("profileCreate.email_placeholder")}
                    defaultValue={store.mainAppStore.profileEmail}
                    onChange={(e) =>
                      store.mainAppStore.setProfileEmail(e.target.value)
                    }
                    className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter"
                    required
                  />
                </div>
              ) : (
                <>
                  {/* Name Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-name">
                      {t("profileCreate.name_label")}
                    </label>
                    <input
                      id="profile-name"
                      type="text"
                      placeholder={t("profileCreate.name_placeholder")}
                      defaultValue={store.mainAppStore.profileName}
                      onChange={(e) =>
                        store.mainAppStore.setProfileName(e.target.value)
                      }
                      className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter"
                      required
                    />
                  </div>

                  {/* Email Field */}
                  {isAppleLogin ? (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-email">
                        {t("profileCreate.email_label")}
                      </label>
                      <input
                        id="profile-email"
                        type="email"
                        placeholder={t("profileCreate.email_placeholder")}
                        defaultValue={store.mainAppStore.profileEmail}
                        onChange={(e) =>
                          store.mainAppStore.setProfileEmail(e.target.value)
                        }
                        className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter"
                        required
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-email">
                        {t("profileCreate.email_label")}
                      </label>
                      <input
                        id="profile-email"
                        type="email"
                        defaultValue={
                          store.mainAppStore.profileEmail ||
                          user?.emailAddresses[0].emailAddress
                        }
                        disabled
                        className="h-[50px] px-4 py-3 bg-[#ebecf0] rounded-md border border-[#efefef] text-base font-medium font-inter"
                        required
                      />
                    </div>
                  )}
                </>
              )}

              {!emailOnly && (
                <>
                  {/* Current Level Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-level">
                      {t("profileCreate.current_level_label")}
                    </label>
                    <select
                      id="profile-level"
                      className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter appearance-none"
                      defaultValue=""
                      onChange={(e) =>
                        store.mainAppStore.setKnowledgeLevel(e.target.value)
                      }
                      required
                    >
                      <option value="" disabled>
                        {t("profileCreate.select_item_placeholder")}
                      </option>
                      {KNOWLEDGE_LEVELS.map((level) => (
                        <option key={level.value} value={level.value}>
                          {t(level.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Training Institute Field */}
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-school">
                        {t("profileCreate.training_institute_label")}
                      </label>
                      <input
                        id="profile-school"
                        type="text"
                        placeholder={t("profileCreate.training_institute_placeholder")}
                        className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter"
                        onChange={(e) =>
                          store.mainAppStore.setProfileSchool(e.target.value)
                        }
                        required
                      />
                    </div>
                    <span className="text-[#666666] text-[10px] font-normal font-inter">
                      {t("profileCreate.training_institute_helper")}
                    </span>
                  </div>

                  {/* Exam Booking Section */}
                  <ExamBookingSection
                    hasBookedExam={hasBookedExam}
                    onBookedChange={(value) => {
                      setHasBookedExam(value);
                      setExamDateRaw("");
                    }}
                    dateInputValue={examDateRaw}
                    onDateChange={setExamDateRaw}
                    minDate={moment().format("YYYY-MM-DD")}
                    maxDate={moment().add(5, "years").format("YYYY-MM-DD")}
                    minMonth={moment().format("YYYY-MM")}
                    maxMonth={moment().add(5, "years").format("YYYY-MM")}
                    variant="create"
                  />

                  {/* Referral Source Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="profile-referral-source">
                      {t("profileCreate.referral_label")}
                    </label>
                    <select
                      id="profile-referral-source"
                      className="h-[50px] px-4 py-3 bg-white rounded-md border border-[#efefef] text-base font-medium font-inter appearance-none"
                      defaultValue=""
                      onChange={(e) =>
                        store.mainAppStore.setProfileReferralSource(
                          e.target.value
                        )
                      }
                      required
                    >
                      <option value="" disabled>
                        {t("profileCreate.select_item_placeholder")}
                      </option>

                      {REFFERAL_VALUES.map((refferal) => (
                        <option key={refferal.value} value={refferal.value}>
                          {t(refferal.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="dp-button-primary h-[51px] px-8 py-4 text-center text-base font-inter"
            >
              {APP_TO_BUILD === "electrical_us" && !standalone
                ? isSubmitting
                  ? t("profileCreate.saving_button")
                  : t("profileCreate.save_button")
                : isSubmitting
                  ? t("profileCreate.creating_button")
                  : t("profileCreate.create_button")}
            </button>
          </form>
        </div>
      </div>

      <Dialog open={welcomeModalOpen} onOpenChange={toggleWelcomeModalOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-gray-100 backdrop-blur-sm" />
          <DialogContent
            onPointerDownOutside={(event) => event.preventDefault()}
            className="fixed left-[50%] top-[50%] z-50 w-[calc(100vw-2rem)] max-w-[412px] translate-x-[-50%] translate-y-[-50%] border bg-background duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] p-6 bg-white rounded-lg shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle className="flex !justify-center">
                {t("profileCreate.welcome_modal_title", {
                  name: store.mainAppStore.profileName,
                })}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-base flex !justify-center font-normal font-inter text-zinc-500 mt-1">
              {t("profileCreate.welcome_modal_body")}
            </DialogDescription>
            <DialogFooter className="flex items-center !justify-center gap-2">
              <button
                onClick={() => navigate(postProfileRoute)}
                className="dp-button-primary px-4 py-2 text-sm font-medium font-inter"
              >
                {t("common.continue")}
              </button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
}

const CreateProfile = observer(function CreateProfile({
  standalone,
}: {
  standalone?: boolean;
}) {
  const isMobileWeb = useIsMobileWeb();
  const store = useRootStore();
  const { t } = useTranslation();
  const navigationLocked = requiresPaidProfileCompletion(
    store.mainAppStore.newPurchaseState?.purchaseAccess,
    store.mainAppStore.profilePersistedComplete,
  );

  if (standalone) {
    return (
      <>
        <CreateProfileView standalone navigationLocked={navigationLocked} />
        {isMobileWeb && !navigationLocked && <DownloadAppCta />}
      </>
    );
  }

  if (navigationLocked) {
    return (
      <main className="flex h-dvh min-h-0 w-full min-w-0 flex-col overflow-hidden bg-white">
        <OnboardingHeader title={t("profile.create_profile")} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CreateProfileView navigationLocked />
        </div>
      </main>
    );
  }

  if (!isMobileWeb) {
    return (
      <BasePage activeItem="home" contentClassName="md:pt-0">
        <main className="min-h-full w-full min-w-0 bg-white">
          <CreateProfileView />
        </main>
      </BasePage>
    );
  }

  return (
    <main className="min-h-screen w-full min-w-0 bg-white">
      <CreateProfileView />
      <DownloadAppCta />
    </main>
  );
});

export default CreateProfile;
